import { computed, effectScope, type Ref } from 'vue'

import { tryUseNuxtApp, useCookie, useNuxtApp, useState } from 'nuxt/app'

import { useLocationStore } from '@common/location-layer/store/location.store'

import { LocationFeatureFlagsService } from '../services/location-feature-flags.service'

import { useLogger } from './useLogger'

/** Temporary Levelup-2026 master flag — remove after launch. Enable: `document.cookie = 'cp-levelup-2026=true; path=/'` */
const COOKIE_KEY = 'cp-levelup-2026'
const API_ENABLED_STATE_KEY = 'cp-levelup-2026-api-enabled'
const RESOLVED_FOR_LOCATION_STATE_KEY = 'cp-levelup-2026-resolved-for-location'

const isTruthyFlag = (value: unknown): boolean => {
  if (value === true || value === 1) return true
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return normalized === 'true' || normalized === '1'
}

/** Reads `featureFlags.clientPortal.isLevelUp2026Enabled` from the preview-cache payload. */
const readLevelUp2026FromFlags = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') return false
  let root = payload as Record<string, unknown>
  if (!root.featureFlags && root.data && typeof root.data === 'object') {
    root = root.data as Record<string, unknown>
  }
  const flags = root.featureFlags && typeof root.featureFlags === 'object' ? (root.featureFlags as Record<string, unknown>) : root
  const clientPortal = flags.clientPortal && typeof flags.clientPortal === 'object' ? (flags.clientPortal as Record<string, unknown>) : null
  return isTruthyFlag(clientPortal?.isLevelUp2026Enabled)
}

type NuxtAppWithLevelUp = NonNullable<ReturnType<typeof tryUseNuxtApp>> & {
  _levelUp2026Cookie?: Ref<string | null>
  _levelUp2026InFlight?: Promise<boolean>
}

/**
 * One cookie ref per Nuxt app. Route middleware reads this flag through `useAppAccess` without an
 * effect scope; memoizing avoids stacking cookie listeners on every navigation.
 * Must run inside Nuxt context (setup / runWithContext).
 */
const getLevelUp2026Cookie = (nuxtApp: NuxtAppWithLevelUp): Ref<string | null> => {
  nuxtApp._levelUp2026Cookie ??= effectScope(true).run(() =>
    useCookie<string | null>(COOKIE_KEY, {
      default: () => null,
      sameSite: 'lax',
      path: '/',
    })
  )
  return nuxtApp._levelUp2026Cookie as Ref<string | null>
}

const getApiEnabled = () => useState<boolean>(API_ENABLED_STATE_KEY, () => false)
const getResolvedForLocationId = () => useState<string | null>(RESOLVED_FOR_LOCATION_STATE_KEY, () => null)

/**
 * Resolve LevelUp for portal `locationStore.locationId` (deduped in-flight).
 * Call only from `useLocation` after portal location is set. Uses `runWithContext`
 * so it is safe after `await` in fetchLocation.
 */
export const ensureLevelUp2026Resolved = async (): Promise<boolean> => {
  const nuxtApp = tryUseNuxtApp() as NuxtAppWithLevelUp | null
  if (!nuxtApp) return false

  if (nuxtApp._levelUp2026InFlight) return nuxtApp._levelUp2026InFlight

  const resolveInContext = async (): Promise<boolean> => {
    const levelUp2026Cookie = getLevelUp2026Cookie(nuxtApp)
    const apiEnabled = getApiEnabled()
    const resolvedForLocationId = getResolvedForLocationId()

    const markResolved = (locationId: string, enabled: boolean) => {
      apiEnabled.value = enabled
      resolvedForLocationId.value = locationId
    }

    // Cookie is client-only QA opt-in; never short-circuit SSR on cookie.
    if (import.meta.client && isTruthyFlag(levelUp2026Cookie.value)) {
      const loc = useLocationStore().locationId || 'cookie'
      markResolved(loc, true)
      return true
    }

    const locationId = useLocationStore().locationId || null
    if (!locationId) {
      apiEnabled.value = false
      resolvedForLocationId.value = null
      return false
    }

    if (resolvedForLocationId.value === locationId) {
      return apiEnabled.value === true
    }

    const { logger } = useLogger()
    try {
      const payload = await new LocationFeatureFlagsService().fetchFeatureFlags(locationId)
      const enabled = readLevelUp2026FromFlags(payload) === true
      markResolved(locationId, enabled)
      logger.info('[LevelUp2026] featureFlags loaded', {
        labels: {
          context: 'ensureLevelUp2026Resolved',
          locationId,
          isLevelUp2026Enabled: String(enabled),
        },
      })
      return enabled
    } catch (error) {
      logger.warn('[LevelUp2026] featureFlags fetch failed', {
        labels: { context: 'ensureLevelUp2026Resolved', locationId },
        error: error instanceof Error ? error : new Error(String(error)),
      })
      markResolved(locationId, false)
      return false
    }
  }

  const resolve = async (): Promise<boolean> => {
    try {
      // Tests / partial Nuxt doubles may omit runWithContext; real apps always have it.
      if (typeof nuxtApp.runWithContext !== 'function') return await resolveInContext()
      return await nuxtApp.runWithContext(() => resolveInContext())
    } finally {
      nuxtApp._levelUp2026InFlight = undefined
    }
  }

  nuxtApp._levelUp2026InFlight = resolve()
  return nuxtApp._levelUp2026InFlight
}

/**
 * Temporary master UI gate for Levelup-2026. Delete after launch.
 *
 * `useLocation` calls `ensureLevelUp2026Resolved` after portal location is set.
 * Consumers only read `isLevelUp2026Enabled` (fail closed until resolved).
 *
 * Enabled when:
 * - preview-cache `featureFlags.clientPortal.isLevelUp2026Enabled` for portal location, or
 * - (client only) cookie `cp-levelup-2026` is true
 */
export const useLevelUp2026 = () => {
  const nuxtApp = useNuxtApp() as NuxtAppWithLevelUp
  const levelUp2026Cookie = getLevelUp2026Cookie(nuxtApp)
  const apiEnabled = getApiEnabled()
  const resolvedForLocationId = getResolvedForLocationId()
  const locationStore = useLocationStore()

  const isLevelUp2026Enabled = computed(() => {
    if (import.meta.client && isTruthyFlag(levelUp2026Cookie.value)) return true
    const loc = locationStore.locationId
    if (!loc || resolvedForLocationId.value !== loc) return false
    return apiEnabled.value === true
  })

  return {
    isLevelUp2026Enabled,
  }
}
